import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, Loader2, Pencil, Plus, Trash2, Sparkles, Play, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Celebrate, EmptyState, SkeletonList } from "@/components/Mascot";
import { useAiJob, isStopped } from "@/lib/aiJob";
import { useAuth } from "@/context/AuthContext";
import { localToday, daysUntil, fmtDay, countdownLabel } from "@/lib/dates";
import { ExamDialog } from "@/components/ExamDialog";
import { PracticeTabs } from "@/components/PracticeTabs";

function dotClass(isToday, finished) {
  if (isToday) return "bg-sky-400 border-sky-300";
  return finished ? "bg-emerald-400 border-emerald-300" : "bg-[#0B0E17] border-white/25";
}

function dayLabelClass(isToday, past) {
  if (isToday) return "text-sky-300";
  return past ? "text-neutral-500" : "";
}

function countdownText(n) {
  if (n < 0) return "This exam has passed";
  if (n === 0) return "Exam day. Good luck!";
  return `${n} ${n === 1 ? "day" : "days"} to go`;
}

function PlanIcon({ busy, remake }) {
  if (busy) return <Loader2 className="h-4 w-4 animate-spin" />;
  return remake ? <RotateCcw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />;
}

function ExamList({ exams, selected, onAdd }) {
  const upcoming = exams.filter((e) => daysUntil(e.date) >= 0);
  const past = exams.filter((e) => daysUntil(e.date) < 0).reverse();
  const row = (e) => (
    <Link key={e.exam_id} to={`/app/exams/${e.exam_id}`} data-testid={`exam-list-item-${e.exam_id}`}
      className={`block px-3 py-2.5 rounded-xl text-sm ${selected === e.exam_id ? "bg-white/10" : "hover:bg-white/5"}`}>
      <p className="truncate">{e.name}</p>
      <p className="text-xs text-neutral-500">{fmtDay(e.date)} · {daysUntil(e.date) < 0 ? "done" : countdownLabel(e.date)}</p>
    </Link>
  );
  return (
    <div className="space-y-4" data-testid="exam-list">
      <button onClick={onAdd} className="w-full flex items-center justify-center gap-2 h-10 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500" data-testid="exams-add-btn"><Plus className="h-4 w-4" /> Add exam</button>
      <div className="space-y-1">{upcoming.length ? upcoming.map(row) : <p className="text-xs text-neutral-500 px-3">No upcoming exams.</p>}</div>
      {past.length > 0 && (
        <div data-testid="past-exams">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-600 px-3 mb-1">Past exams</p>
          <div className="space-y-1 opacity-70">{past.map(row)}</div>
        </div>
      )}
    </div>
  );
}

function TaskRow({ exam, task, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/exams/${exam.exam_id}/tasks/${task.task_id}`, { done: !task.done }); onChange(data); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const job = useAiJob();
  const start = async () => {
    const topic = task.topic || exam.name;
    if (task.kind === "chat") {
      navigate("/app/new", { state: { draft: `Help me revise ${topic} for my ${exam.name} exam. Explain the key ideas step by step, then check I've understood.` } });
      return;
    }
    if (busy) { job.stop(); setBusy(false); return; }
    setBusy(true);
    try { const { data } = await job.run((o) => http.post("/practice", { topic: `${exam.subject || exam.name} — ${topic}` }, o)); navigate("/app/practice", { state: { set: data } }); }
    catch (e) { if (!isStopped(e) && e.response?.status !== 402) toast.error(formatError(e)); setBusy(false); }
  };
  return (
    <li className="flex items-center gap-3 py-2" data-testid={`plan-task-${task.task_id}`}>
      <button onClick={toggle} disabled={busy} data-testid={`plan-task-tick-${task.task_id}`}
        className={`h-6 w-6 shrink-0 rounded-full border flex items-center justify-center transition-colors ${task.done ? "bg-emerald-400 border-emerald-400 text-black" : "border-white/25 hover:border-emerald-300"}`}>
        {task.done && <Check className="h-3.5 w-3.5" />}
      </button>
      <span className={`flex-1 text-sm ${task.done ? "line-through text-neutral-500" : ""}`}>{task.text}</span>
      {!task.done && (
        <button onClick={start} className="text-xs px-3 py-1.5 rounded-full border border-sky-300/30 text-sky-300 hover:bg-sky-500/10 flex items-center gap-1 shrink-0" data-testid={`plan-task-start-${task.task_id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} {busy ? "Stop" : "Start"}
        </button>
      )}
    </li>
  );
}

function PlanTimeline({ exam, onChange }) {
  const today = localToday();
  const all = exam.plan.days.flatMap((d) => d.tasks);
  return (
    <>
    {all.length > 0 && all.every((t) => t.done) && <div className="mb-6"><Celebrate testid="plan-celebrate" title="Revision plan complete!" text={`Every task for ${exam.name} is done. You're ready.`} /></div>}
    <ol className="relative border-l border-white/10 ml-3 space-y-6" data-testid="plan-timeline">
      {exam.plan.days.map((d) => {
        const isToday = d.date === today;
        const finished = d.tasks.length > 0 && d.tasks.every((t) => t.done);
        return (
          <li key={d.date} className={`pl-6 relative ${finished ? "opacity-50" : ""}`} data-testid={`plan-day-${d.date}`}>
            <span className={`absolute -left-[7px] top-1.5 h-3.5 w-3.5 rounded-full border-2 ${dotClass(isToday, finished)}`} />
            <p className={`text-sm font-medium ${dayLabelClass(isToday, d.date < today)}`}>{isToday ? "Today" : fmtDay(d.date)}</p>
            <ul className={`mt-1 ${isToday ? "rounded-xl border border-sky-400/30 bg-sky-500/[0.05] px-3" : ""}`}>
              {d.tasks.map((t) => <TaskRow key={t.task_id} exam={exam} task={t} onChange={onChange} />)}
            </ul>
          </li>
        );
      })}
      <li className="pl-6 relative" data-testid="plan-exam-day">
        <span className="absolute -left-[7px] top-1.5 h-3.5 w-3.5 rounded-full bg-yellow-300 border-2 border-yellow-200" />
        <p className="text-sm font-medium text-yellow-200">{fmtDay(exam.date)} · Exam day. Good luck!</p>
      </li>
    </ol>
    </>
  );
}

function PlanButton({ exam, onChange }) {
  const { ent, refreshEnt } = useAuth();
  const [busy, setBusy] = useState(false);
  const locked = ent && !ent.features.practice;
  const job = useAiJob({ onStopped: refreshEnt });
  const make = async () => {
    if (busy) { job.stop(); setBusy(false); return; }
    if (locked) { window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "feature", feature: "practice", needs: "starter" } })); return; }
    setBusy(true);
    try { const { data } = await job.run((o) => http.post(`/exams/${exam.exam_id}/plan`, { today: localToday() }, o)); onChange(data); toast.success("Revision plan ready"); }
    catch (e) { if (!isStopped(e) && e.response?.status !== 402) toast.error(formatError(e)); } finally { setBusy(false); refreshEnt(); }
  };
  const remake = !!exam.plan;
  return (
    <button onClick={make} data-testid="plan-make-btn"
      className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 disabled:opacity-50 ${remake ? "border border-white/15 hover:bg-white/5" : "bg-sky-400 text-white hover:bg-sky-500"}`}>
      <PlanIcon busy={busy} remake={remake} />
      {busy ? "Stop" : `${remake ? "Remake plan" : "Make my revision plan"} · 1 message`}
      {locked && <span className="flex items-center gap-0.5 text-[10px] opacity-70"><Lock className="h-2.5 w-2.5" />Starter</span>}
    </button>
  );
}

function ExamDetail({ exam, onChange, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const n = daysUntil(exam.date);
  const del = async () => {
    if (!window.confirm("Delete this exam and its plan?")) return;
    try { await http.delete(`/exams/${exam.exam_id}`); onDeleted(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="exam-detail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs text-neutral-500">{exam.subject || "Exam"} · {fmtDay(exam.date)}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="exam-detail-name">{exam.name}</h2>
          <p className={`font-display text-xl mt-1 ${n <= 3 && n >= 0 ? "text-yellow-300" : "text-sky-300"}`} data-testid="exam-detail-countdown">
            {countdownText(n)}
          </p>
          {exam.topics.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{exam.topics.map((t) => <span key={t} className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-neutral-300">{t}</span>)}</div>}
        </div>
        <div className="flex gap-1">
          <button onClick={() => setEditing(true)} className="p-2 text-neutral-400 hover:text-white" data-testid="exam-edit-btn"><Pencil className="h-4 w-4" /></button>
          <button onClick={del} className="p-2 text-neutral-400 hover:text-red-400" data-testid="exam-delete-btn"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {n > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <PlanButton exam={exam} onChange={onChange} />
          {exam.plan_stale && <span className="text-xs text-yellow-200" data-testid="plan-stale-note">You changed this exam. Remake the plan to match.</span>}
        </div>
      )}
      {exam.plan && n >= 0 && <PlanTimeline exam={exam} onChange={onChange} />}
      {!exam.plan && n > 0 && <p className="text-sm text-neutral-500">Your plan will cover every day from today until the exam, mixing these topics with your weak spots.</p>}
      {editing && <ExamDialog exam={exam} onClose={() => setEditing(false)} onSaved={(d) => { setEditing(false); onChange(d); }} />}
    </div>
  );
}

export default function ExamsPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exams, setExams] = useState(null);
  const [adding, setAdding] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("action") !== "add") return;
    setAdding(true);
    setParams({}, { replace: true });
  }, [params, setParams]);
  const load = useCallback(() => http.get("/exams").then((r) => setExams(r.data)).catch((e) => { toast.error(formatError(e)); setExams([]); }), []);
  useEffect(() => { load(); }, [load]);
  const update = (d) => setExams((x) => x.map((e) => (e.exam_id === d.exam_id ? d : e)));
  const exam = exams?.find((e) => e.exam_id === examId) || exams?.find((e) => daysUntil(e.date) >= 0);
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
        <PracticeTabs />
        <div className="grid md:grid-cols-[240px_1fr] gap-8">
          <aside className="space-y-4">
            {exams && <ExamList exams={exams} selected={exam?.exam_id} onAdd={() => setAdding(true)} />}
          </aside>
          <main className="min-w-0">
            {!exams && <SkeletonList rows={2} />}
            {exams && !exam && <EmptyState testid="exams-empty" pose="thinking" title="No exams yet" text="Add your first exam to get a countdown and a day-by-day revision plan." />}
            {exam && <ExamDetail key={exam.exam_id} exam={exam} onChange={update} onDeleted={() => { navigate("/app/exams"); load(); }} />}
          </main>
        </div>
      </div>
      {adding && <ExamDialog onClose={() => setAdding(false)} onSaved={(d) => { setAdding(false); setExams((x) => [...x, d].sort((a, b) => a.date.localeCompare(b.date))); navigate(`/app/exams/${d.exam_id}`); }} />}
    </div>
  );
}
